import { useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'

import SvgIcon from '@/components/svg-icon/svgIcon'
import { Typography } from '@/components/typography/typography'
import { fontStyles } from '@/components/typography/typographyUtils'
import { Reservation } from '@/features/reservation'
import { cn } from '@/libs/tw'
import { useAppConfigStore } from '@/stores/app-store-config/appConfigStore'
import { translate } from '@/utils/translator'

import { useNewsletter } from '../../hooks/useNewsletter'
import { FormValues } from '../../schema'

type LegalProps = {
	roomStays: Reservation['reservation']['roomStays']
}

export const Legal = ({ roomStays }: LegalProps) => {
	const { legalTextUrls } = useAppConfigStore()
	const { data: newsletter } = useNewsletter()

	const { control } = useFormContext<FormValues>()
	const { field } = useController({
		name: 'newsLetter',
		control,
	})

	const parseDataProcessingText = () => {
		const parts = translate('guest-data-summary-legal-text-processing').split('##')

		return parts.map((part) => {
			if (part === '!~*') {
				return (
					<LegalLink
						label={translate('guest-data-summary-legal-text-data-protection')}
						url={legalTextUrls.privacyPolicyUrl}
					/>
				)
			}

			if (part === '!~**') {
				return <ConditionOfSalesPopUp roomStays={roomStays} />
			}

			if (part === '!~***') {
				return (
					<LegalLink
						label={translate('guest-data-summary-legal-text-hotel-contract')}
						url={legalTextUrls.hotelConditionUrl}
					/>
				)
			}

			if (part === '!~****') {
				return (
					<LegalLink
						label={translate('guest-data-summary-legal-text-parking-garages-contract')}
						url={legalTextUrls.parkingConditionUrl}
					/>
				)
			}

			return (
				<Typography style={'p'} element="span">
					{part}
				</Typography>
			)
		})
	}

	return (
		<>
			{newsletter?.showEmailNewsLetterContent && (
				<div className={cn('flex gap-16')}>
					<Checkbox checked={Boolean(field.value)} onChange={field.onChange} />
					<div
						className="cursor-pointer"
						onClick={(e) => {
							const target = e.target as HTMLElement
							if (target.tagName !== 'A') {
								field.onChange(!field.value)
							}
						}}
					>
						<Typography style={'p'} element="span">
							{newsletter.newsletterContent && (
								<div
									className="[&_a]:underline"
									dangerouslySetInnerHTML={{
										__html: newsletter.newsletterContent,
									}}
								/>
							)}
						</Typography>
					</div>
				</div>
			)}
			<Typography style={'p-bold'} element="h4">
				{translate('guest-data-summary-legal-title')}
			</Typography>
			<div>{parseDataProcessingText()}</div>
		</>
	)
}

type LegalLinkProps = {
	label: string
	url: string
}

const LegalLink = ({ label, url }: LegalLinkProps) => (
	<a href={url} target="_blank" className={cn(fontStyles.p, 'underline')}>
		{label}
	</a>
)

const ConditionOfSalesPopUp = ({ roomStays }: LegalProps) => {
	const [isOpen, setIsOpen] = useState(true)

	const onClose = () => {
		setIsOpen(false)
		setTimeout(() => {
			setIsOpen(true)
		}, 500)
	}

	return (
		<>
			<div className={cn('group relative inline')}>
				<Typography style={'p'} element="span" className="underline">
					{translate('guest-data-summary-legal-text-conditions-of-sale')}
				</Typography>
				{isOpen && (
					<div
						className={cn(
							'fixed left-0 top-1/2 hidden -translate-y-1/2 rounded-8 group-hover:flex  md:absolute md:top-0 md:-translate-y-full',
							'w-screen flex-col gap-18 bg-[#E4E4E4] p-24 text-userData-legal-popup md:w-[600px]'
						)}
					>
						<div className={cn('flex justify-end')}>
							<button onClick={onClose}>
								<SvgIcon
									iconName="rounded-cross"
									className="h-24 w-24 text-current"
								/>
							</button>
						</div>
						<Typography style={'h3'} element="p">
							{translate('guest-data-summary-legal-text-conditions-of-sale')}
						</Typography>
						{roomStays.map((room, index) => (
							<>
								<Typography style={'p-bold'} element="p">
									{translate('guest-data-room', [index + 1])}{' '}
									{room.ratePlan.title}
								</Typography>
								{room.freeCancellationDeadline && room.cancellationPolicy ? (
									<Typography style={'p'} element="p">
										<div
											dangerouslySetInnerHTML={{
												__html: room.cancellationPolicy,
											}}
										/>
									</Typography>
								) : (
									<>
										<Typography style={'p-bold'} element="p">
											{translate(
												'guest-data-summary-legal-cancelation-title'
											)}
										</Typography>
										<Typography style={'p'} element="p">
											{translate(
												'guest-data-summary-legal-text-no-cancelation'
											)}
										</Typography>
									</>
								)}
							</>
						))}
					</div>
				)}
			</div>
		</>
	)
}

type CheckboxProps = {
	checked: boolean
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onChange: (...event: any[]) => void
}

const Checkbox = ({ checked, onChange }: CheckboxProps) => {
	return (
		<div className={cn('relative inline-block h-[30px] w-[30px]')}>
			<input
				onKeyDown={(e) => e.key === 'Enter' && onChange(!checked)}
				type="checkbox"
				checked={checked}
				onChange={onChange}
				className={cn(
					'h-[30px] w-[30px] cursor-pointer appearance-none rounded-2 border border-userData-checkBox-secondary md:border-userData-checkBox-primary'
				)}
			/>
			{checked && (
				<div
					className={cn(
						'pointer-events-none absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 transform text-userData-legal-check '
					)}
				>
					<SvgIcon iconName={'check'} className="h-24 w-24" />
				</div>
			)}
		</div>
	)
}
